import { ArrowRightOutlined, PlusOutlined } from '@ant-design/icons'
import { StoreOrderStatusCell } from '@components/StoreMonitoring/StoreDashboard/Grid/StoreOrderStatusCell'
import { GraviButton, Texto } from '@gravitate-js/excalibrr'
import { ReadableDifference } from '@utils/general'
import { getStoreNumberComparator } from '@utils/grid'
import { Tooltip } from 'antd'
import moment from 'moment'
import { forwardRef } from 'react'

import { AnalyticIcon, TankAlertType } from '../TankAlertDisplay/components/AnalyticIcon'
import { FlagCellRenderer } from './FlagCellRenderer'
import { TimeDeadlineFilter, noFitOptions } from '@/pages/OrderManagement/components/Grid/Filters/TimeDeadline'
import {
  doesSiteNoFitFilterPass,
  doesSiteRunoutFilterPass,
} from '@/pages/OrderManagement/components/Grid/Filters/TimeDeadline/customFilters'
import type { IRowNode } from 'ag-grid-community'
import type { FlagIconProps } from '../TankAlertDisplay/components/icons'

type MinNoFitColumnProps = {
  data: Gravitate.Site
  value: Gravitate.Site['retain']
  size?: FlagIconProps['size']
}

export function minRunoutComparator(
  a: IRowNode<Gravitate.Site>['data'],
  b: IRowNode<Gravitate.Site>['data'],
  rundownToggled: boolean
) {
  if (a?.monitoring_strategy === 'No Readings (Call-In)') return 1
  if (b?.monitoring_strategy === 'No Readings (Call-In)') return -1
  const sortKey = rundownToggled ? 'soonest_run_out_with_drops' : 'soonest_run_out'
  const valA = a?.[sortKey]
  const valB = b?.[sortKey]
  if (!valA && !valB) return 0
  if (!valA) return 1
  if (!valB) return -1
  if (valA === valB) return 0
  return valA > valB ? 1 : -1
}

export function MinNoFitColumn({ data, value, size = 'small' }: MinNoFitColumnProps) {
  const potentialNoFit = data.flags?.find((flag) => flag.name.toLowerCase() === 'potential no fit')
  const noFit = data.flags?.find((flag) => flag.name.toLowerCase() === 'no fit')
  const noFitIcon = noFit || potentialNoFit
  if (!data.retain || data.monitoring_strategy === 'No Readings (Call-In)') {
    return null
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      {noFitIcon && (
        <div style={{ marginRight: '4px' }}>
          <AnalyticIcon title={noFitIcon.name} threshold={noFitIcon?.threshold} size={size} />
        </div>
      )}
      {ReadableDifference(value.retain_time || data.retain.retain_time, undefined, false, false, false)}
    </div>
  )
}

export const getColumnDefs = (openDrawer, showModalWithStore, canCreateOrder, rundownToggled, setStoreList) => [
  {
    cellRenderer: 'agGroupCellRenderer',
    field: 'id',
    valueFormatter: () => '',
    headerName: '',
    maxWidth: 50,
    filter: false,
    getQuickFilterText: () => '',
  },
  {
    headerName: 'Order Status',
    field: 'order_status',
    width: 175,
    valueGetter: ({ data }) => {
      if (data.order_status === 'unset') {
        return 'Unassigned'
      }
      if (data.order_status === 'in progress') {
        return 'In Progress'
      }
      if (data.order_status === 'assigned') {
        return 'Scheduled'
      }
      return 'No Orders'
    },
    cellRenderer: ({ value, data }) => {
      return (
        <StoreOrderStatusCell
          status={data.order_status}
          storeId={data.id}
          openDrawer={openDrawer}
          hasOrder={data.has_order}
        />
      )
    },
  },
  {
    headerName: 'Flags',
    field: 'flags',
    flex: 0,
    minWidth: 80,
    valueGetter: ({ data }) => data.flags?.map((flag) => flag.name),
    cellRenderer: ({ data }) => {
      const showStoreFlags = data.flags && data.flags.length > 0
      return showStoreFlags && <FlagCellRenderer hideRunout={false} flags={data.flags} hideOrderNumber />
    },
  },
  {
    headerName: 'Number',
    field: 'store_number',
    cellRenderer: ({ value }) => {
      return (
        <Texto weight='bold' category='h5'>
          {value}
        </Texto>
      )
    },
    comparator: getStoreNumberComparator('store_number'),
  },
  {
    headerName: 'Name',
    field: 'name',
  },
  {
    headerName: 'Min. No Fit',
    dataIndex: 'retain',
    field: 'retain',
    width: 110,
    filter: forwardRef((props, ref) => (
      <TimeDeadlineFilter {...props} ref={ref} doesFilterPass={doesSiteNoFitFilterPass} filterOptions={noFitOptions} />
    )),
    cellRenderer: ({ value, data }) => <MinNoFitColumn data={data} value={value} />,
    comparator: (a, b, nodeA, nodeB) => {
      if (nodeA?.data?.monitoring_strategy === 'No Readings (Call-In)') {
        return 1
      }
      if (nodeB?.data?.monitoring_strategy === 'No Readings (Call-In)') {
        return -1
      }
      return moment(nodeA?.data?.retain?.retain_time).isBefore(nodeB.data?.retain?.retain_time) ? 1 : -1
    },
  },
  {
    headerName: 'Min. Runout',
    dataIndex: 'soonest_run_out',
    field: 'soonest_run_out',
    width: 110,
    filterParams: { accessor: rundownToggled ? 'soonest_run_out_with_drops' : 'soonest_run_out' },
    filter: forwardRef((props, ref) => (
      <TimeDeadlineFilter
        {...props}
        ref={ref}
        includeDrops={rundownToggled}
        doesFilterPass={doesSiteRunoutFilterPass}
      />
    )),
    valueGetter: ({ data }) => (rundownToggled ? data?.soonest_run_out_with_drops : data?.soonest_run_out),
    cellRenderer: ({ value, data }) => {
      const potentialRunout = data.flags.find((flag) => flag.name.toLowerCase() === 'potential runout')
      const runoutWithinX = data.flags
        .filter((flag) => flag?.type?.toLowerCase() === 'run_out')
        .sort((a, b) => (a.severity === 'error' ? -1 : 1))[0]
      const empty = data.flags.find((flag) => flag.name.toLowerCase() === 'empty')
      const runoutAlert = empty || runoutWithinX || potentialRunout

      // Tad hacky, but we need to handle the runout alerts being configurable
      let runoutIcon = runoutAlert?.name
      let threshold
      if (runoutAlert?.name.toLowerCase().includes('runout within')) {
        runoutIcon =
          runoutAlert.severity == 'error' ? TankAlertType.RunoutWithinError : TankAlertType.RunoutWithinWarning
        threshold = runoutAlert?.threshold
      }

      const iconValidWithDrops = runoutAlert?.details[0]?.with_drops
      const showIconWithDrops = rundownToggled && iconValidWithDrops
      const showIconWithoutDrops = !rundownToggled && runoutIcon

      if (data.monitoring_strategy === 'No Readings (Call-In)') {
        return null
      }
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            fontWeight: runoutIcon === 'Empty' ? 700 : 'auto',
          }}
        >
          <div style={{ marginRight: '4px' }}>
            {(showIconWithDrops || showIconWithoutDrops) && (
              <AnalyticIcon title={runoutIcon} threshold={threshold} size='small' />
            )}
          </div>
          {ReadableDifference(value, undefined, true, false, false)}
        </div>
      )
    },
    sort: 'asc',
    comparator: (_, __, nodeA: IRowNode<Gravitate.Site>, nodeB: IRowNode<Gravitate.Site>) =>
      minRunoutComparator(nodeA?.data, nodeB?.data, rundownToggled),
  },

  {
    headerName: 'Address',
    field: 'address',
    cellRenderer: ({ value }) => <Texto className='text-truncate'>{value}</Texto>,
  },
  {
    headerName: 'City',
    field: 'city',
  },
  {
    headerName: 'State',
    field: 'state',
  },
  {
    headerName: 'Counterparty',
    field: 'site_counterparty',
    cellRenderer: ({ value }) => <Texto className='text-truncate'>{value}</Texto>,
  },
  {
    field: 'actions',
    headerName: '',
    filter: false,
    cellRenderer: ({ data }) => (
      <div className='flex items-center justify-center'>
        {canCreateOrder && (
          <Tooltip title='Create Order'>
            <GraviButton
              style={{ height: 25, padding: '0px 15px' }}
              theme2
              icon={<PlusOutlined />}
              className='mr-3'
              onClick={() => showModalWithStore(data)}
            />
          </Tooltip>
        )}
        <GraviButton
          style={{ height: 25, padding: '0px 15px' }}
          icon={<ArrowRightOutlined />}
          error={data.has_alert}
          onClick={() => setStoreList(data.id)}
          buttonText='Details'
          data-cy='details-btn'
        />
      </div>
    ),
    minWidth: 180,
    lockPosition: 'right',
  },
  {
    headerName: 'Customer',
    field: 'customer',
    hide: true,
  },
]
